"use client";

import { useState, type FormEvent } from "react";
import { useAction } from "convex/react";
import { api } from "@convex/_generated/api";
import { EMAIL_FORMAT } from "@shared/formats/email";
import { Button } from "@/components/ui/button";
import type { AuthEntryPoint } from "@/lib/analytics/types";
import { track } from "@/lib/analytics/tracker";
import type { SignedIn } from "@/lib/auth/sign-in/result";
import { describeError } from "@/lib/handle-error";
import { signInLinkHere } from "@/lib/auth/emailed-sign-in";
import { sendFailureReason, trackAuthFailure } from "@/lib/auth/sign-in/auth-tracking";
import { CodeEntryForm, SIGN_IN_CODE_LENGTH } from "../code-entry-form";
import { signInWithEmailCode } from "../sign-in-actions";
import { AuthField } from "./auth-field";
import { AuthError } from "./auth-messages";
import { useAuthCompletion } from "./use-auth-completion";
import { useSignInCodeDelivery } from "../use-sign-in-code-delivery";

interface EmailCodeStepProps {
  entryPoint: AuthEntryPoint;
  initialEmail?: string;
  initialCode?: string;
  quoteClaimToken?: string;
  onSignedIn: (result: SignedIn) => void | Promise<void>;
  onNeedsCode: () => void;
  onBack: (email: string) => void;
}

export function useSendCode(entryPoint: AuthEntryPoint) {
  const sendCode = useAction(api.auth.magicCode.sendSignInCode);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const delivery = useSignInCodeDelivery({ entryPoint, method: "magic_code" });
  const send = async (email: string): Promise<string | null> => {
    const trimmed = email.trim();
    if (!EMAIL_FORMAT.validate(trimmed)) {
      setError(EMAIL_FORMAT.errorMessage);
      return null;
    }
    setIsSending(true);
    setError(null);
    delivery.setSent(null);
    try {
      track("auth.started", { entry_point: entryPoint, method: "magic_code" });
      const result = await sendCode({ email: trimmed, link: signInLinkHere("modal") });
      delivery.setSent(result);
      return result.email;
    } catch (cause) {
      trackAuthFailure({ entryPoint, method: "magic_code" }, "send", sendFailureReason(cause));
      setError(describeError(cause, "We couldn't send a code. Please try again."));
      return null;
    } finally {
      setIsSending(false);
    }
  };
  const reset = () => {
    setError(null);
    delivery.setSent(null);
  };
  return { error: error ?? delivery.error, setError, reset, isSending, send };
}

function EmailForm(props: {
  initialEmail: string;
  sending: ReturnType<typeof useSendCode>;
  onSent: (email: string) => void;
  onBack: (email: string) => void;
}) {
  const [email, setEmail] = useState(props.initialEmail);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const sentTo = await props.sending.send(email);
    if (sentTo) props.onSent(sentTo);
  };
  return (
    <form className="space-y-3" onSubmit={submit}>
      <AuthField label="Email" type="email" autoComplete="email" autoFocus value={email} onChange={setEmail} />
      <AuthError message={props.sending.error} />
      <Button type="submit" variant="primary" className="w-full" disabled={props.sending.isSending}>
        {props.sending.isSending ? "Sending code…" : "Email me a code"}
      </Button>
      <Button type="button" variant="ghost" className="w-full" onClick={() => props.onBack(email)}>
        Log in with a password instead
      </Button>
    </form>
  );
}

export function EmailCodeStep(props: EmailCodeStepProps) {
  const sending = useSendCode(props.entryPoint);
  const [emailedCode, setEmailedCode] = useState(props.initialCode);
  const [sentTo, setSentTo] = useState<string | null>(
    props.initialCode && props.initialEmail ? props.initialEmail : null,
  );
  const completion = useAuthCompletion({
    entryPoint: props.entryPoint,
    surface: "magic_code",
    method: "magic_code",
    onSignedIn: props.onSignedIn,
    onNeedsCode: props.onNeedsCode,
  });
  if (!sentTo) {
    return <EmailForm initialEmail={props.initialEmail ?? ""} sending={sending} onSent={setSentTo} onBack={props.onBack} />;
  }
  return (
    <CodeEntryForm
      initialCode={emailedCode}
      intro={
        <p className="text-sm text-muted-foreground">
          {emailedCode
            ? `Log in as ${sentTo}.`
            : `Enter the ${SIGN_IN_CODE_LENGTH}-digit code we emailed to ${sentTo}.`}
        </p>
      }
      isVerifying={completion.isBusy}
      error={completion.error ?? sending.error}
      submitLabel="Log in"
      busyLabel="Logging in…"
      onVerify={(code) =>
        completion.run(() => signInWithEmailCode({ email: sentTo, code, quoteClaimToken: props.quoteClaimToken }))
      }
      onResend={async () => (await sending.send(sentTo)) !== null}
      onChangeEmail={() => {
        setEmailedCode(undefined);
        setSentTo(null);
        sending.reset();
      }}
      onEdit={completion.clearError}
    />
  );
}
